import {
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, InputAdornment } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import { PaymentType } from '../../../types';
import { getCurrencySymbol } from '../../../utils/currency-helpers';
import {
  cvsFeeScheduleFormSchema,
  CvsFeeScheduleFormValues,
  DEFAULT_CVS_FEE_SCHEDULE_FORM_VALUES,
} from './validation-schema';

export const CVS_FEE_SCHEDULE_FORM_TEST_ID = 'cvs-fee-schedule-form';
export const CVS_FEE_SCHEDULE_FORM_SUBMIT_TEST_ID =
  'cvs-fee-schedule-form-submit-button';
export const CVS_FEE_SCHEDULE_FORM_CANCEL_TEST_ID =
  'cvs-fee-schedule-form-cancel-button';
export const CVS_FEE_SCHEDULE_FORM_PAYMENT_TYPE_INPUT_TEST_ID =
  'cvs-fee-schedule-form-payment-type-input';
export const CVS_FEE_SCHEDULE_FORM_PRICE_INPUT_TEST_ID =
  'cvs-fee-schedule-form-price-input';
export const CVS_FEE_SCHEDULE_FORM_UPC_INPUT_TEST_ID =
  'cvs-fee-schedule-form-upc-input';
export const CVS_FEE_SCHEDULE_FORM_CHARGE_INPUT_TEST_ID =
  'cvs-fee-schedule-form-charge-input';
export const CVS_FEE_SCHEDULE_FORM_DAYS_SUPPLY_INPUT_TEST_ID =
  'cvs-fee-schedule-form-days-supply-input';
export const CVS_FEE_SCHEDULE_FORM_DESCRIPTION_INPUT_TEST_ID =
  'cvs-fee-schedule-form-description-input';

interface CvsFeeScheduleFormProps {
  onSubmit: (values: CvsFeeScheduleFormValues) => void | Promise<void>;
  initialValues?: CvsFeeScheduleFormValues;
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

const NONE_OPTION = { code: '', name: '(none)' } as PaymentType;

export const CvsFeeScheduleForm = ({
  onSubmit,
  initialValues,
  paymentTypes,
  paymentTypesLoading,
}: CvsFeeScheduleFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();

  const paymentTypeOptions = [
    NONE_OPTION,
    ...[...paymentTypes].sort((a, b) => a.code.localeCompare(b.code)),
  ];

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
  } = useForm<CvsFeeScheduleFormValues>({
    mode: 'onChange',
    resolver: yupResolver(cvsFeeScheduleFormSchema),
    defaultValues: DEFAULT_CVS_FEE_SCHEDULE_FORM_VALUES,
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (
    values: CvsFeeScheduleFormValues,
  ): Promise<void> => {
    await onSubmit(values);
  };

  return (
    <form data-testid={CVS_FEE_SCHEDULE_FORM_TEST_ID}>
      <HHStack spacing={6} direction="column">
        <Controller
          control={control}
          name="paymentType"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={
                paymentTypeOptions.find(pt => pt.code === value) ?? NONE_OPTION
              }
              options={paymentTypeOptions}
              getOptionLabel={(option): string =>
                option.code === '' ? '(none)' : option.code
              }
              isOptionEqualToValue={(option, val): boolean =>
                option.code === val?.code
              }
              onChange={(_, newValue): void => onChange(newValue?.code ?? '')}
              disableClearable
              loading={paymentTypesLoading}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label="Payment Type"
                  error={!!errors.paymentType}
                  helperText={errors.paymentType?.message}
                  inputProps={{ ...params.inputProps, name: 'paymentType' }}
                  data-testid={CVS_FEE_SCHEDULE_FORM_PAYMENT_TYPE_INPUT_TEST_ID}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="price"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="CVS Price"
              type="number"
              value={value ?? ''}
              onChange={onChange}
              error={!!errors.price}
              helperText={errors.price?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    disableTypography
                    sx={{ color: 'gray' }}
                  >
                    {getCurrencySymbol()}
                  </InputAdornment>
                ),
              }}
              inputProps={{ role: 'spinbutton', name: 'price' }}
              data-testid={CVS_FEE_SCHEDULE_FORM_PRICE_INPUT_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="upc"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="UPC"
              value={value}
              onChange={onChange}
              error={!!errors.upc}
              helperText={errors.upc?.message}
              inputProps={{ role: 'textbox', name: 'upc' }}
              data-testid={CVS_FEE_SCHEDULE_FORM_UPC_INPUT_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="charge"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="AWP"
              type="number"
              value={value}
              onChange={(e): void => onChange(e.target.value)}
              error={!!errors.charge}
              helperText={errors.charge?.message}
              inputProps={{ role: 'spinbutton', name: 'charge' }}
              data-testid={CVS_FEE_SCHEDULE_FORM_CHARGE_INPUT_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="daysSupply"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Days Supply"
              value={value}
              onChange={onChange}
              error={!!errors.daysSupply}
              helperText={errors.daysSupply?.message}
              inputProps={{ role: 'textbox', name: 'daysSupply' }}
              data-testid={CVS_FEE_SCHEDULE_FORM_DAYS_SUPPLY_INPUT_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Description"
              value={value}
              onChange={onChange}
              error={!!errors.description}
              helperText={errors.description?.message}
              multiline
              rows={3}
              inputProps={{ role: 'textbox', name: 'description' }}
              data-testid={CVS_FEE_SCHEDULE_FORM_DESCRIPTION_INPUT_TEST_ID}
            />
          )}
        />

        <HHStack direction="row" spacing={2} justifyContent="flex-end">
          <HHButton
            hhVariant="variant-bypass"
            variant="text"
            onClick={handleCancel}
            type="button"
            disabled={isSubmitting}
            data-testid={CVS_FEE_SCHEDULE_FORM_CANCEL_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHLoadingButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            disabled={!isValid || !isDirty || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit(handleFormSubmit)}
            data-testid={CVS_FEE_SCHEDULE_FORM_SUBMIT_TEST_ID}
          >
            Submit
          </HHLoadingButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
